import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import CloseIcon from "@mui/icons-material/Close";

import {
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetAllGroupsQuery,
  useUpdateGroupMutation,
} from "../../redux/admin/groupApi";
import AddDeviceToGroupModal from "./AddDeviceToGroupModal";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getRowsPerPageLabel,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";

const defaultForm = {
  id: "",
  name: "",
  contact: "",
  groupType: "fleet",
};

const getGroupContact = (group) =>
  group?.description?.trim() || group?.owner?.mobileNo || group?.owner?.email || "-";

const GroupsList = () => {
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [form, setForm] = useState(defaultForm);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState("");
  const [assignGroup, setAssignGroup] = useState(null);

  const queryParams = useMemo(
    () => ({
      page: rowsPerPage === -1 ? 1 : page,
      limit: rowsPerPage === -1 ? 1000 : rowsPerPage,
      search: search.trim() || undefined,
    }),
    [page, rowsPerPage, search]
  );

  const { data, isLoading, isFetching, refetch } = useGetAllGroupsQuery(queryParams);
  const [createGroup, { isLoading: creating }] = useCreateGroupMutation();
  const [updateGroup, { isLoading: updating }] = useUpdateGroupMutation();
  const [deleteGroup, { isLoading: deleting }] = useDeleteGroupMutation();

  const groups = Array.isArray(data?.data) ? data.data : [];
  const totalCount = Number(data?.pagination?.totalCount || groups.length || 0);
  const totalPages = rowsPerPage === -1 ? 1 : Math.max(1, Number(data?.pagination?.totalPages || Math.ceil(totalCount / rowsPerPage) || 1));
  const displayRange = useMemo(() => getDisplayRange(totalCount, page, rowsPerPage), [page, rowsPerPage, totalCount]);
  const busy = creating || updating;

  const openCreate = () => {
    setForm(defaultForm);
    setFormOpen(true);
  };

  const openEdit = (group) => {
    setForm({
      id: group?._id || "",
      name: group?.name || "",
      contact: group?.description || "",
      groupType: group?.groupType || "fleet",
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setForm(defaultForm);
    setFormOpen(false);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setStatus({ type: "error", message: "Group name is required." });
      return;
    }

    try {
      const payload = {
        name: form.name.trim(),
        description: form.contact.trim(),
        groupType: form.groupType || "fleet",
      };

      if (form.id) {
        await updateGroup({ id: form.id, ...payload }).unwrap();
        setStatus({ type: "success", message: "Group updated successfully." });
      } else {
        await createGroup(payload).unwrap();
        setStatus({ type: "success", message: "Group created successfully." });
      }

      closeForm();
      refetch();
    } catch (error) {
      setStatus({ type: "error", message: error?.data?.msg || "Failed to save group." });
    }
  };

  const handleDelete = async () => {
    if (!deleteGroupId) return;
    try {
      await deleteGroup({ id: deleteGroupId }).unwrap();
      setDeleteGroupId("");
      setStatus({ type: "success", message: "Group deleted successfully." });
      refetch();
    } catch (error) {
      setStatus({ type: "error", message: error?.data?.msg || "Failed to delete group." });
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Paper sx={{ borderRadius: 2, overflow: "hidden", borderTop: "4px solid #4f9cf9" }}>
        <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e5e7eb" }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Groups</Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={refetch}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ p: 2 }}>
          {status.message ? (
            <Alert severity={status.type || "info"} sx={{ mb: 2 }}>
              {status.message}
            </Alert>
          ) : null}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" }, gap: 2, alignItems: "center", mb: 2 }}>
            <TextField
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "#fb7185" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#fff",
                },
              }}
            />

            <Tooltip title="Add Group">
              <IconButton onClick={openCreate} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827", width: 48, height: 48 }}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={rowsPerPage}
                onChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(1);
                }}
                MenuProps={ROWS_PER_PAGE_MENU_PROPS}
              >
                {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {getRowsPerPageLabel(size)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Refresh">
              <IconButton onClick={refetch}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <TableContainer sx={{ minHeight: 420, borderTop: "1px solid #d1d5db", borderBottom: "1px solid #e5e7eb" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "#6b7280" }}>Group Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#6b7280" }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#6b7280", width: 180 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8, color: "#6b7280" }}>
                      Loading groups...
                    </TableCell>
                  </TableRow>
                ) : groups.length ? (
                  groups.map((group) => (
                    <TableRow key={group._id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{group.name || "-"}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{getGroupContact(group)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit Group">
                            <IconButton size="small" onClick={() => openEdit(group)} sx={{ bgcolor: "#2563eb", borderRadius: 1 }}>
                              <EditOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Assign Device">
                            <IconButton size="small" onClick={() => setAssignGroup(group)} sx={{ bgcolor: "#2563eb", borderRadius: 1 }}>
                              <DirectionsCarFilledOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Group">
                            <IconButton size="small" onClick={() => setDeleteGroupId(group._id)} sx={{ bgcolor: "#ef4444", borderRadius: 1 }}>
                              <DeleteOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8, color: "#6b7280" }}>
                      No groups found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 1, pt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ color: "#374151" }}>
              Showing {displayRange.from} to {displayRange.to} of {totalCount} entries
            </Typography>
            <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
          </Box>

          {isFetching && !isLoading ? (
            <Typography sx={{ mt: 1.5, color: "#6b7280", fontSize: 13 }}>Updating groups...</Typography>
          ) : null}
        </Box>
      </Paper>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}>
        <DialogTitle sx={{ px: 2.5, py: 2, fontSize: 18, fontWeight: 800, borderTop: "4px solid #4f9cf9", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {form.id ? "Edit Group" : "Add Group"}
          <IconButton onClick={closeForm}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            <Box>
              <Typography sx={{ mb: 1, fontSize: 16, fontWeight: 700 }}>Name</Typography>
              <TextField
                fullWidth
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </Box>
            <Box>
              <Typography sx={{ mb: 1, fontSize: 16, fontWeight: 700 }}>Contact</Typography>
              <TextField
                fullWidth
                value={form.contact}
                onChange={(event) => setForm((prev) => ({ ...prev, contact: event.target.value }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "flex-start", gap: 2 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={busy} sx={{ minWidth: 180, py: 1.4, fontWeight: 800, bgcolor: "#fb4b6b", "&:hover": { bgcolor: "#e83d5d" } }}>
            {busy ? "SAVING..." : form.id ? "UPDATE" : "SUBMIT"}
          </Button>
          <Button variant="contained" onClick={closeForm} sx={{ minWidth: 180, py: 1.4, fontWeight: 800, bgcolor: "#6b7280", "&:hover": { bgcolor: "#4b5563" } }}>
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteGroupId)} onClose={() => setDeleteGroupId("")} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete Group</DialogTitle>
        <DialogContent>
          <Typography>Is group ko delete karna hai?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteGroupId("")}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <AddDeviceToGroupModal
        groupId={assignGroup?._id || ""}
        initialGroupDevices={Array.isArray(assignGroup?.devices) ? assignGroup.devices : []}
        isOpen={Boolean(assignGroup?._id)}
        onClose={() => setAssignGroup(null)}
      />
    </Box>
  );
};

export default GroupsList;
